import { FC, useEffect, useState } from 'react'
import Modal from '@common/Modal'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import cn from 'classnames'
import { useAddAlert } from '@/hooks/useAddAlert'
import { directionGroupApi, usersAPI } from '@/api'
import Preloader from '@UI/Preloader'
import { ElClassFormDataType } from '../PageContent/PageContent'
import Select from '@pages/Admin/CRM/components/base/Select/Select'
import { ensureApiSuccess } from '@/helpers/ensureApiSuccess'
import { getApiErrorMessage } from '@/helpers/getApiErrorMessage'

interface EditTeacherProps {
  group?: NonNullable<ElClassFormDataType['groups']>[number] | undefined
  franchiseId?: number | null
  open: boolean
  onClose: () => void
  onSuccess: (seminarian: {
    id: number
    name: string
    avatar_path: string
  }) => void
}

interface EditTeacherContentProps extends EditTeacherProps {}

const EditTeacherModalContent: FC<EditTeacherContentProps> = ({
  group,
  franchiseId,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)

  const [teachers, setTeachers] = useState<any[]>([])

  useEffect(() => {
    if (group === null) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка при получении списка педагогов',
        type: 'error',
      })
      onClose()
      return
    }

    if (!group?.direction?.id) return

    setLoading(true)
    usersAPI
      .getColleaguesForGroup({
        directionId: group.direction.id,
        ...(franchiseId ? { franchise_ids: [franchiseId] } : {}),
      })
      .then(ensureApiSuccess)
      .then((res) => {
        setTeachers(
          (res?.data?.data ?? [])
            .filter((item: { isTeacher: boolean }) => item.isTeacher)
            .map((item: { id: string; name: string }) => ({
              label: item.name,
              value: Number(item.id),
            }))
        )
      })
      .catch((err) => {
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка при получении списка педагогов',
          type: 'error',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [franchiseId, group?.direction?.id])

  // forms

  const [data, setData] = useState<{
    teacher_id: number | null
  }>({ teacher_id: group?.seminarian?.id || null })

  useEffect(() => {
    if (!open) return

    setData({
      teacher_id:
        group?.seminarian?.id != null ? Number(group.seminarian.id) : null,
    })
  }, [group?.id, group?.seminarian?.id, open])

  const handlerData = (key: keyof typeof data, value: any) => {
    setData((prev) => ({ ...prev, [key]: value }))
  }

  const handlerSubmit = () => {
    if (data.teacher_id == null) {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: 'Педагог не выбран',
      })
      return
    }

    setSubmitting(true)
    directionGroupApi
      .updGroupTeacher({
        id: group?.id,
        teacher_id: data.teacher_id,
      })
      .then(ensureApiSuccess)
      .then((res) => {
        const responseData = res?.data?.data ?? res?.data
        if (responseData?.teacher_id == null) {
          throw new Error('Сервер не подтвердил изменение педагога')
        }

        const selectedTeacher = teachers.find(
          (teacher) => Number(teacher.value) === Number(data.teacher_id)
        )

        onSuccess && onSuccess({
          id: Number(responseData.teacher_id),
          name: responseData?.teacher_name ?? selectedTeacher?.label ?? '',
          avatar_path: '',
        })
        addAlert({
          title: 'Успешно',
          text: `Педагог успешно изменен`, // в group?.name устаревшие данные
          type: 'success',
        })
        onClose()
      })
      .catch((err) => {
        addAlert({
          title: 'Ошибка',
          text: getApiErrorMessage(
            err,
            'Ошибка при изменении педагога группы'
          ),
          type: 'error',
        })
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <>
      <div className={cn(mclasses.modalContainer)}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              {group?.seminarian?.id
                ? 'Изменить педагога'
                : 'Выберите педагога группы'}
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Preloader fullScreen="app" />
          </div>
        ) : (
          <div className={cn(mclasses.formWrapper)}>
            <div>
              <p className={mclasses.label}>Педагог</p>
              <Select
                showSearch
                className={cn(iclasses.newSelect)}
                popupClassName={iclasses.newSelectPopup}
                placeholder="Выберите педагога"
                value={data.teacher_id ? Number(data.teacher_id) : null}
                onChange={(value) => handlerData('teacher_id', value)}
                options={teachers}
              />
            </div>
          </div>
        )}
        <div className={mclasses.actions}>
          <Button color="gray" onClick={() => onClose()}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={handlerSubmit}
            disabled={data.teacher_id == null || submitting || loading}
            isLoading={submitting}
          >
            Подтвердить
          </Button>
        </div>
      </div>
    </>
  )
}

const EditTeacherModal: FC<EditTeacherProps> = (props) => {
  const { open, onClose } = props

  return (
    <>
      <Modal
        active={open}
        className={mclasses.modalWrapper as any}
        setActive={() => onClose()}
      >
        <EditTeacherModalContent {...props} />
      </Modal>
    </>
  )
}

export default EditTeacherModal
